import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useModules } from "@/hooks/useModules";

export type PricingTier = "insight" | "orchestration" | "infrastructure";

export const TIER_LABELS: Record<PricingTier, string> = {
  insight: "Insight",
  orchestration: "Orchestration",
  infrastructure: "Intelligence Infrastructure",
};

export const TIER_PRICES: Record<PricingTier, string> = {
  insight: "$499–$999/mo",
  orchestration: "$1,500–$3,000/mo",
  infrastructure: "$4,000–$8,000/mo",
};

export const TIER_TAGLINES: Record<PricingTier, string> = {
  insight: "Visibility",
  orchestration: "Orchestration",
  infrastructure: "Predictive Infrastructure",
};

export type Feature =
  | "members_overview"
  | "leadership_contributions"
  | "trust_credibility"
  | "member_segments"
  | "emn_insights"
  | "event_planner"
  | "event_matchmaking"
  | "event_roi"
  | "cohorts"
  | "growth_tracks"
  | "program_analytics"
  | "network_health"
  | "engagement_analytics"
  | "renewal_intelligence"
  | "reports"
  | "admin_scoring"
  | "admin_roles"
  | "admin_sponsors"
  | "admin_integrations"
  | "admin_emn_policies"
  | "programming_intelligence"
  // Core metric features
  | "growth_participation_rate"
  | "network_activation_rate"
  | "growth_momentum_index"
  | "member_value_score"
  | "renewal_risk_index"
  | "connection_velocity"
  | "programming_impact_score"
  // Infrastructure-only
  | "predictive_modeling"
  | "sector_segmentation"
  | "economic_cluster_mapping"
  | "api_access"
  | "custom_board_dashboards"
  | "multi_org_benchmarking";

/**
 * Tier 1 — Insight: Visibility
 * Core Dashboard, Growth Participation Rate, Network Activation Rate,
 * Basic Member Profiles, Quarterly Board Report.
 * No predictive risk. No programming engine.
 */
const INSIGHT_FEATURES: Feature[] = [
  "members_overview",
  "leadership_contributions",
  "event_planner",
  "admin_roles",
  "admin_sponsors",
  "growth_participation_rate",
  "network_activation_rate",
  "reports", // quarterly board report
];

/**
 * Tier 2 — Orchestration: Intervention engine
 * All Insight features + Growth Momentum Index, Member Value Score,
 * Renewal Risk Index, Programming Intelligence Engine, Action Directives,
 * Monthly executive report export.
 */
const ORCHESTRATION_FEATURES: Feature[] = [
  ...INSIGHT_FEATURES,
  "trust_credibility",
  "member_segments",
  "emn_insights",
  "event_matchmaking",
  "event_roi",
  "cohorts",
  "growth_tracks",
  "program_analytics",
  "network_health",
  "engagement_analytics",
  "renewal_intelligence",
  "admin_scoring",
  "admin_integrations",
  "admin_emn_policies",
  "programming_intelligence",
  "growth_momentum_index",
  "member_value_score",
  "renewal_risk_index",
  "connection_velocity",
  "programming_impact_score",
];

/**
 * Tier 3 — Intelligence Infrastructure: Predictive
 * All Orchestration features + Predictive modeling, Sector segmentation,
 * Economic cluster mapping, API integrations, Custom board dashboards.
 */
const INFRASTRUCTURE_FEATURES: Feature[] = [
  ...ORCHESTRATION_FEATURES,
  "predictive_modeling",
  "sector_segmentation",
  "economic_cluster_mapping",
  "api_access",
  "custom_board_dashboards",
  "multi_org_benchmarking",
];

const TIER_FEATURES: Record<PricingTier, Feature[]> = {
  insight: INSIGHT_FEATURES,
  orchestration: ORCHESTRATION_FEATURES,
  infrastructure: INFRASTRUCTURE_FEATURES,
};

// Which tier unlocks a feature (for upgrade prompts)
export const FEATURE_TIER: Record<Feature, PricingTier> = {
  // Insight (Tier 1)
  members_overview: "insight",
  leadership_contributions: "insight",
  event_planner: "insight",
  admin_roles: "insight",
  admin_sponsors: "insight",
  growth_participation_rate: "insight",
  network_activation_rate: "insight",
  reports: "insight",

  // Orchestration (Tier 2)
  trust_credibility: "orchestration",
  member_segments: "orchestration",
  emn_insights: "orchestration",
  event_matchmaking: "orchestration",
  event_roi: "orchestration",
  cohorts: "orchestration",
  growth_tracks: "orchestration",
  program_analytics: "orchestration",
  network_health: "orchestration",
  engagement_analytics: "orchestration",
  renewal_intelligence: "orchestration",
  admin_scoring: "orchestration",
  admin_integrations: "orchestration",
  admin_emn_policies: "orchestration",
  programming_intelligence: "orchestration",
  growth_momentum_index: "orchestration",
  member_value_score: "orchestration",
  renewal_risk_index: "orchestration",
  connection_velocity: "orchestration",
  programming_impact_score: "orchestration",

  // Intelligence Infrastructure (Tier 3)
  predictive_modeling: "infrastructure",
  sector_segmentation: "infrastructure",
  economic_cluster_mapping: "infrastructure",
  api_access: "infrastructure",
  custom_board_dashboards: "infrastructure",
  multi_org_benchmarking: "infrastructure",
};

interface TierContextType {
  tier: PricingTier;
  setTier: (tier: PricingTier) => void;
  hasFeature: (feature: Feature) => boolean;
}

const TierContext = createContext<TierContextType | null>(null);

/**
 * Map legacy Feature keys to new capability_keys.
 * Used for backward compatibility with FeatureGate.
 */
export const FEATURE_TO_CAPABILITY: Partial<Record<Feature, string>> = {
  members_overview: "membership.tiers",
  leadership_contributions: "membership.tiers",
  member_segments: "membership.tiers",
  trust_credibility: "membership.tiers",
  event_planner: "events.create",
  event_matchmaking: "events.surveys",
  event_roi: "events.reporting",
  cohorts: "programs.cohorts",
  growth_tracks: "programs.multi_session",
  program_analytics: "programs.reporting",
  network_health: "community.graph",
  engagement_analytics: "community.graph",
  renewal_intelligence: "membership.retention",
  reports: "executive.strategic",
  emn_insights: "community.intros",
  admin_scoring: "membership.tiers",
  admin_roles: "core.permissions",
  admin_sponsors: "sponsors.packages",
  admin_integrations: "core.workspace",
  admin_emn_policies: "community.intros",
  programming_intelligence: "programs.reporting",
  growth_participation_rate: "membership.tiers",
  network_activation_rate: "community.graph",
  growth_momentum_index: "membership.tiers",
  member_value_score: "membership.tiers",
  renewal_risk_index: "membership.retention",
  connection_velocity: "community.pipeline",
  programming_impact_score: "programs.reporting",
  predictive_modeling: "executive.growth_gaps",
  sector_segmentation: "executive.growth_gaps",
  economic_cluster_mapping: "executive.growth_gaps",
  api_access: "executive.strategic",
  custom_board_dashboards: "executive.strategic",
  multi_org_benchmarking: "cross_org.benchmarking",
};

export const TierProvider = ({ children }: { children: ReactNode }) => {
  const [tier, setTier] = useState<PricingTier>("infrastructure");
  const { hasCapability } = useModules();

  // Delegate to module capability system when a mapping exists,
  // otherwise fall back to tier-based check.
  const hasFeature = useCallback(
    (feature: Feature) => {
      const capKey = FEATURE_TO_CAPABILITY[feature];
      if (capKey) return hasCapability(capKey);
      return TIER_FEATURES[tier].includes(feature);
    },
    [tier, hasCapability],
  );

  const value = useMemo(() => ({ tier, setTier, hasFeature }), [tier, hasFeature]);

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

export const useTier = () => {
  const ctx = useContext(TierContext);
  if (!ctx) throw new Error("useTier must be used within TierProvider");
  return ctx;
};
